import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { useNavigate, useParams } from 'react-router-dom';
import { Heading } from '@common/SideBar';
import { DescriptionOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../../styles';
import { Card } from '@common/Card';
import { Box, Stack } from '@mui/material';
import { Label } from '@common/Label';

const Container = styled.div`
  display: flex;
  flex-direction: column;

  a {
    font-size: 14px;
    word-wrap: break-word;
    color: ${({ theme }) => theme.primary};
  }
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
`;

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  p,
  pre {
    margin: 0;
    color: ${({ theme }) => theme.text.standard};
    font-size: 14px;
  }
`;

export const formatSEO = (body: any) => {
  try {
    const full_regex = /---\ntitle:[\s\S]*?---/g;
    const title_regex = /title:[\s\S]*?\n/g;
    const url_regex = /url:[\s\S]*?\n/g;
    const description_regex = /description:[\s\S]*?\n/g;
    const keywords_regex = /keywords:[\s\S]*?---/g;

    const result = body.match(full_regex);
    const seo = result?.[0];
    const title = seo.match(title_regex);
    const url = seo.match(url_regex);
    const description = seo.match(description_regex);
    const keywords = seo.match(keywords_regex);

    return {
      title: title?.[0]?.replace('title: ', '').replace(/\\|\"|\'|\n/g, '') || null,
      description: description?.[0]?.replace('description: ', '').replace(/\\|\"|\'|\n/g, '') || null,
      url: url?.[0]?.replace('url: ', '').replace(/\\|\"|\'|\n/g, '') || null,
      keywords:
        keywords?.[0]
          ?.replace('keywords: ', '')
          .replace(/\\|\"|\'/g, '')
          .replace('---', '') || 'No Keywords',
      body: body.replace(full_regex, '').replace(/^\s+/, ''),
    };
  } catch (e) {
    return {
      title: null,
      description: null,
      url: null,
      keywords: null,
      body,
    };
  }
};

interface Props {
  file: any;
  error: Error | null;
  contentType: string;
  isLoading: boolean;
}

export const FileSidebar: React.FC<Props> = ({ file, error, contentType, isLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { service_id, bucketName, fileName } = useParams();
  const { title, description, url } = formatSEO(file);

  return (
    <Container>
      <Heading
        icon={<DescriptionOutlined height={14} />}
        title={fileName as string}
        onClose={() => {
          navigate(`/${service_id}/buckets/${bucketName}`);
        }}
      />
      <Cards>
        <Card
          icon={<DescriptionOutlined sx={{ color: theme.text.standard }} />}
          body={
            <BodyContainer>
              <Stack spacing='8px'>
                <Label title='Bucket Name' tooltip='This is the bucket the file exist inside.' />
                <p>{bucketName}</p>
              </Stack>
              {!error && (
                <Stack spacing='8px'>
                  <Label title='Content Type' tooltip='This is content type of the file.' />
                  {isLoading ? (
                    <Box sx={{ color: theme.text.standard }}>
                      <DotLoader height={8} />
                    </Box>
                  ) : (
                    <p>{contentType}</p>
                  )}
                </Stack>
              )}
              {title && (
                <Stack spacing='8px'>
                  <Label title='Title' tooltip='The title of this document, written in the SEO.' />
                  <p>{title}</p>
                </Stack>
              )}
              {description && (
                <Stack spacing='8px'>
                  <Label title='Description' tooltip='The description of this document, written in the SEO.' />
                  <p>{description}</p>
                </Stack>
              )}
              {url && (
                <Stack spacing='8px'>
                  <Label title='URL' tooltip='The url of this document.' />
                  <a href={url} target='_blank' rel='noreferrer'>
                    {url}
                  </a>
                </Stack>
              )}
            </BodyContainer>
          }
        />
      </Cards>
    </Container>
  );
};
